import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
	faChevronLeft,
	faFloppyDisk,
	faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import MainButton from '../MainButton/MainButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import {
	allowNextFormLeave,
	consumeFormLeaveBypass,
} from '../../hooks/useFormDirty';

type FormSaveActionsProps = {
	isDirty: boolean;
	isSubmitting: boolean;
	onSave: () => void;
	onSaveAndLeave: () => void;
	onCancel?: () => void;
	extraActions?: React.ReactNode;
	className?: string;
};

type PendingLeave = {
	url: string;
	replace: boolean;
};

/** Un cran d'historique, sinon le chemin de repli (fiche ouverte directement). */
export function navigateBack(navigate: NavigateFunction, fallback: () => void) {
	const idx = (window.history.state as { idx?: number } | null)?.idx;
	if (typeof idx === 'number' && idx > 0) {
		navigate(-1);
		return;
	}
	fallback();
}

const FormSaveActions: React.FC<FormSaveActionsProps> = ({
	isDirty,
	isSubmitting,
	onSave,
	onSaveAndLeave,
	onCancel,
	extraActions,
	className = 'flex justify-end mt-6 space-x-2',
}) => {
	const navigate = useNavigate();
	const disabled = !isDirty || isSubmitting;
	const isDirtyRef = useRef(isDirty);
	isDirtyRef.current = isDirty;
	const [confirmOpen, setConfirmOpen] = useState(false);
	const pendingRef = useRef<PendingLeave | null>(null);
	const restoringHistoryRef = useRef(false);

	useEffect(() => {
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!isDirtyRef.current) return;
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);

		const originalPush = history.pushState.bind(history);
		const originalReplace = history.replaceState.bind(history);

		const samePlace = (url: string) => {
			const current =
				window.location.pathname +
				window.location.search +
				window.location.hash;
			return url === current || url.endsWith(current);
		};

		const guard =
			(replace: boolean, original: History['pushState']) =>
			(data: unknown, unused: string, url?: string | URL | null) => {
				if (consumeFormLeaveBypass() || !isDirtyRef.current) {
					return original(data, unused, url);
				}
				const next = url == null ? '' : String(url);
				if (!next || samePlace(next)) {
					return original(data, unused, url);
				}
				pendingRef.current = { url: next, replace };
				setConfirmOpen(true);
			};

		history.pushState = guard(false, originalPush) as History['pushState'];
		history.replaceState = guard(
			true,
			originalReplace
		) as History['replaceState'];

		const onPopState = (event: PopStateEvent) => {
			if (restoringHistoryRef.current) {
				restoringHistoryRef.current = false;
				return;
			}
			if (consumeFormLeaveBypass() || !isDirtyRef.current) return;
			event.stopImmediatePropagation();
			const destination =
				window.location.pathname +
				window.location.search +
				window.location.hash;
			pendingRef.current = { url: destination, replace: false };
			restoringHistoryRef.current = true;
			window.history.go(1);
			setConfirmOpen(true);
		};
		window.addEventListener('popstate', onPopState, true);

		return () => {
			window.removeEventListener('beforeunload', onBeforeUnload);
			window.removeEventListener('popstate', onPopState, true);
			history.pushState = originalPush;
			history.replaceState = originalReplace;
		};
	}, []);

	const confirmLeave = () => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		setConfirmOpen(false);
		if (!pending) return;
		allowNextFormLeave();
		navigate(pending.url, { replace: pending.replace });
	};

	const stayOnForm = () => {
		pendingRef.current = null;
		setConfirmOpen(false);
	};

	return (
		<>
			<ConfirmDialog
				open={confirmOpen}
				title="Modifications non enregistrées"
				description="Des modifications n'ont pas été enregistrées. Voulez-vous quitter cette page ?"
				confirmColor="red"
				onConfirm={confirmLeave}
				onCancel={stayOnForm}
			/>
			<div className={className}>
				{onCancel && (
					<SecondaryButton
						label="Retour"
						icon={faChevronLeft}
						onClick={() => navigateBack(navigate, onCancel)}
						size="lg"
					/>
				)}
				{extraActions}
				<MainButton
					label="Enregistrer"
					icon={faFloppyDisk}
					onClick={onSave}
					size="lg"
					disabled={disabled}
				/>
				<MainButton
					label="Enregistrer et quitter"
					icon={faRightFromBracket}
					onClick={onSaveAndLeave}
					size="lg"
					disabled={disabled}
				/>
			</div>
		</>
	);
};

export default FormSaveActions;
